/* eslint-disable jsx-a11y/label-has-associated-control */
import { Empty } from 'antd';

import { EmptyIcon } from 'assets/icon/bigicon';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { fetchMediaCalendar } from 'state/actions/mediaTimeline';
import './MediaTimeline.css';
import TimelineWrapper from './TimelineWrapper';
import MediaTimelineToolbar from './MediaTimelineToolbar';

dayjs.extend(utc);
dayjs.extend(timezone);

const VIEW_MODES = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
};

const MediaTimelineComponents = ({ searchTimeline, selectedCategory }) => {
  /* =========================
     STATE
  ========================= */
  const [isDailyHours, setIsDailyHours] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [viewMode, setViewMode] = useState(VIEW_MODES.DAILY);
  const [expandedPlaylist, setExpandedPlaylist] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const dispatch = useDispatch();
  const siteId = window.location.pathname.split('/')[1];

  const pickerType = {
    [VIEW_MODES.DAILY]: 'date',
    [VIEW_MODES.WEEKLY]: 'date',
    [VIEW_MODES.MONTHLY]: 'month',
    [VIEW_MODES.YEARLY]: 'year',
  }[viewMode];

  const getToolbarTitle = () => {
    switch (viewMode) {
      case VIEW_MODES.DAILY:
        return dayjs(selectedDate).format('DD MMMM');

      case VIEW_MODES.WEEKLY: {
        const start = dayjs(selectedDate);
        const end = start.add(6, 'day');

        return `${start.format('DD MMM')} - ${end.format('DD MMM')}`;
      }

      case VIEW_MODES.MONTHLY:
        return dayjs(selectedDate).format('MMMM YYYY');

      case VIEW_MODES.YEARLY:
        return dayjs(selectedDate).format('YYYY');

      default:
        return '';
    }
  };
  /* =========================
     DATE RANGE
  ========================= */
  const getDateRange = () => {
    const start = dayjs(selectedDate);
    const end = dayjs(selectedDate);

    switch (viewMode) {
      case VIEW_MODES.WEEKLY:
        return {
          start: start.format('YYYY-MM-DD'),
          end: start.add(6, 'day').format('YYYY-MM-DD'),
        };

      case VIEW_MODES.MONTHLY:
        return {
          start: start.startOf('month').format('YYYY-MM-DD'),
          end: start.endOf('month').format('YYYY-MM-DD'),
        };

      case VIEW_MODES.YEARLY:
        return {
          start: start.startOf('year').format('YYYY-MM-DD'),
          end: start.endOf('year').format('YYYY-MM-DD'),
        };

      case VIEW_MODES.DAILY:
      default:
        return {
          start: start.format('YYYY-MM-DD'),
          end: end.format('YYYY-MM-DD'),
        };
    }
  };

  /* =========================
     FETCH DATA
  ========================= */
  useEffect(() => {
    const load = async () => {
      const { start, end } = getDateRange();

      const res = await dispatch(fetchMediaCalendar({ siteId, start, end }));

      const normalizeCalendarData = (data = {}) => {
        return Object.entries(data).flatMap(([calendarDate, medias]) =>
          medias.map((media) => ({
            ...media,
            calendarDate,

            // media all-day
            start: media.start ?? `${calendarDate}T00:00:00.000Z`,

            end: media.end ?? `${calendarDate}T23:59:59.999Z`,
          }))
        );
      };
      const normalized = normalizeCalendarData(res.data);
      setCalendarData(normalized);
    };

    load();
  }, [dispatch, siteId, selectedDate, viewMode]);

  /* =========================
     GROUPING
  ========================= */
  const groupedPlaylists = useMemo(() => {
    return Object.values(
      calendarData.reduce((acc, item) => {
        if (!acc[item.categoryName]) {
          acc[item.categoryName] = {
            id: item.categoryName,
            name: item.categoryName,
            medias: [],
          };
        }
        acc[item.categoryName].medias.push(item);
        return acc;
      }, {})
    );
  }, [calendarData]);

  /* =========================
     FILTER + SEARCH
  ========================= */
  const categoryId = selectedCategory === 'all' ? '' : selectedCategory;

  const keyword = searchTimeline?.trim().toLowerCase() || '';

  const filtered = groupedPlaylists.filter((p) =>
    !categoryId ? true : p.medias.some((m) => m.categoryId === categoryId)
  );

  const finalData = filtered.map((p) => {
    const medias = p.medias.map((m) => ({
      ...m,
      isMatched: !keyword || m.name.toLowerCase().includes(keyword),
    }));

    return {
      ...p,
      medias,
      hasMatched: medias.some((m) => m.isMatched),
    };
  });

  const handleDateChange = (value) => {
    if (!value) return;
    setSelectedDate(value.toDate());
    setPickerOpen(false);
  };

  const handlePrev = () => {
    setSelectedDate((prev) => {
      const date = dayjs(prev);

      switch (viewMode) {
        case VIEW_MODES.DAILY:
          return date.subtract(1, 'day').toDate();

        case VIEW_MODES.WEEKLY:
          return date.subtract(1, 'week').toDate();

        case VIEW_MODES.MONTHLY:
          return date.subtract(1, 'month').toDate();

        case VIEW_MODES.YEARLY:
          return date.subtract(1, 'year').toDate();

        default:
          return prev;
      }
    });
  };

  const handleNext = () => {
    setSelectedDate((prev) => {
      const date = dayjs(prev);

      switch (viewMode) {
        case VIEW_MODES.DAILY:
          return date.add(1, 'day').toDate();

        case VIEW_MODES.WEEKLY:
          return date.add(1, 'week').toDate();

        case VIEW_MODES.MONTHLY:
          return date.add(1, 'month').toDate();

        case VIEW_MODES.YEARLY:
          return date.add(1, 'year').toDate();

        default:
          return prev;
      }
    });
  };
  useEffect(() => {
    setExpandedPlaylist(null);
  }, [calendarData, searchTimeline, selectedCategory]);
  return (
    <div className="media-timeline">
      <div className="media-timeline-container">
        <MediaTimelineToolbar
          date={{
            pickerOpen,
            setPickerOpen,
            pickerType,
            selectedDate,
            handleDateChange,
            handlePrev,
            handleNext,
            getToolbarTitle,
            isDailyHours,
            setIsDailyHours,
          }}
          view={{
            viewMode,
            setViewMode,
            VIEW_MODES,
          }}
        />

        {finalData.length === 0 && (
          <div className="template-empty">
            <Empty image={<EmptyIcon />} description="No Data" />
          </div>
        )}

        {finalData.length > 0 && (
          <TimelineWrapper
            groupedPlaylists={finalData}
            searchTimeline={searchTimeline}
            expandedPlaylist={expandedPlaylist}
            setExpandedPlaylist={setExpandedPlaylist}
            selectedDate={selectedDate}
            viewMode={viewMode}
            siteId={siteId}
            isDailyHours={isDailyHours}
          />
        )}
      </div>
    </div>
  );
};

export default MediaTimelineComponents;
